/**
 * FP3-029B6B — "Assistants" card surfaced inside the supervising
 * provider's own workspace (Today, Caseload).
 *
 * Renders only for primary providers who actually supervise at
 * least one active assistant. Provides:
 *   - Roster of supervised assistants (name + assistant_type chip)
 *   - "View caseload" / "View schedule" / "View reports" deep-links
 *     into a read-only shadow workspace via `?as_provider={id}` —
 *     handled by the FP3-029B6B-extended ReadOnlyProviderBanner.
 *   - "Message" trigger that opens the supervision DM composer.
 *
 * Does NOT render for assistants viewing their own workspace (they
 * see the supervising-primary chip instead) or for admins (admin
 * has its own Provider Detail surface).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  UsersThree,
  CalendarBlank,
  Envelope,
  GraduationCap,
  X,
  ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { assistantAccountBadge } from "./AssistantAssignmentChip";

export default function AssistantsCard() {
  const { user, provider } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [composerFor, setComposerFor] = useState(null);

  const isProviderUser = user?.permission_role === "provider";
  const myProviderId = provider?.id;

  // FP3-029B6B — Resolve supervised assistants via the dedicated
  // self-fetch endpoint. Any authenticated provider can call —
  // primaries get their own `assistants[]`, assistants get their
  // `supervising_provider` instead. Avoids relying on the admin-
  // only `/admin/providers/{id}` endpoint that 403s for normal
  // providers.
  useEffect(() => {
    if (!isProviderUser || !myProviderId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .getMyAssistants()
      .then((r) => {
        if (cancelled) return;
        setDetail(r.data || null);
      })
      .catch(() => {
        if (cancelled) return;
        setDetail(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isProviderUser, myProviderId]);

  const visibleAssistants = useMemo(() => {
    if (!detail || detail.provider_role_type !== "primary") return [];
    // FP3-101 — Rostered/Invited (pre-activation) assistants are
    // listed with an explicit status badge; only ended supervision
    // hides a row.
    return (detail.assistants || []).filter((a) =>
      a.assignable !== undefined
        ? a.assignable
        : a.supervision_status === "active"
    );
  }, [detail]);

  // Render nothing for non-providers, providers without assistants,
  // and assistants viewing their own workspace.
  if (!isProviderUser) return null;
  if (loading) return null;
  if (visibleAssistants.length === 0) return null;

  return (
    <>
      <Card
        data-testid="caseload-assistants-card"
        className="border-[#E5E3DB] rounded-2xl mb-4 max-w-[1400px]"
      >
        <CardContent className="px-4 py-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="text-[13.5px] font-semibold text-[#1F2937] heading-font flex items-center gap-1.5">
              <UsersThree size={14} weight="duotone" className="text-[#3F4756]" />
              My assistants
              <span
                data-testid="caseload-assistants-count"
                className="ml-1.5 text-[11px] font-medium text-[#6B7280] bg-[#F3F1EC] rounded-full px-2 py-0.5 tabular-nums"
              >
                {visibleAssistants.length}
              </span>
            </h3>
          </div>
          <ul className="divide-y divide-[#F1F2F4]">
            {visibleAssistants.map((a) => {
              const badge = assistantAccountBadge(a);
              const isActivated = !badge;
              return (
              <li
                key={a.user_id}
                data-testid={`caseload-assistant-${a.provider_id}`}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[13px] font-medium text-[#1F2937]">
                      {a.name}
                    </span>
                    <span
                      className="inline-flex items-center text-[10.5px] uppercase tracking-[0.08em] font-medium px-2 py-0.5 rounded-full bg-[#EEF1F6] text-[#3F4756]"
                    >
                      {a.assistant_type || "Assistant"}
                    </span>
                    {badge && (
                      <span
                        data-testid={`caseload-assistant-${a.provider_id}-account-status`}
                        title={badge.title}
                        className="inline-flex items-center text-[10.5px] uppercase tracking-[0.08em] font-semibold px-2 py-0.5 rounded-full bg-[#FDF0DC] text-[#8A6B33] border border-[#E8D5A8]"
                      >
                        {badge.title}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 text-[10.5px] text-[#6B7280]">
                      <GraduationCap size={10} weight="duotone" />
                      {a.caseload_count} student
                      {a.caseload_count === 1 ? "" : "s"}
                    </span>
                  </div>
                  {a.email && (
                    <p className="text-[10.5px] text-[#9AA0A6] mt-0.5 truncate">
                      {a.email}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap shrink-0">
                  {/* FP3-069 follow-up #12 — Removed Caseload + Reports
                      action chips. Pete already sees Max's assigned
                      students on his own Caseload page (they're
                      co-assigned). Reports are not yet truly Max-scoped,
                      so we hide the chip to avoid leaking Pete's data
                      under a "Read-only — Max" banner. The two surfaces
                      that remain — Schedule + Message — are the ones
                      with verified read-only scoping (FP3-069 #12
                      backend + FE work). Reports will return in a
                      later batch once it is supervision-aware.
                      FP3-101 — hidden until the account is activated
                      (no login/schedule/DM exists yet). */}
                  {isActivated && (
                    <Link
                      data-testid={`caseload-assistant-${a.provider_id}-view-schedule`}
                      to={`/schedule?as_provider=${a.provider_id}`}
                      className="inline-flex items-center gap-1 text-[11.5px] font-medium text-[#1E3F33] bg-[#E8F0EB] hover:bg-[#d5e5d9] px-2 py-1 rounded-md"
                      title="Open assistant schedule (read-only)"
                    >
                      <CalendarBlank size={11} weight="duotone" /> Schedule
                    </Link>
                  )}
                  {isActivated && (
                    <Button
                      data-testid={`caseload-assistant-${a.provider_id}-message`}
                      size="sm"
                      variant="outline"
                      onClick={() => setComposerFor(a)}
                      className="h-7 text-[11.5px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB]"
                    >
                      <Envelope size={11} weight="duotone" className="mr-1" />
                      Message
                    </Button>
                  )}
                </div>
              </li>
              );
            })}
          </ul>
          <p className="text-[10.5px] text-[#9AA0A6] mt-2 leading-snug">
            Assistant work remains attributed to the assistant. Open
            Schedule for a read-only supervision view of an assistant's
            calendar; Message for a direct supervision DM.
          </p>
        </CardContent>
      </Card>

      {composerFor && (
        <SupervisionMessageComposer
          recipient={composerFor}
          onClose={() => setComposerFor(null)}
        />
      )}
    </>
  );
}

/**
 * SupervisionMessageComposer — small modal for primary↔assistant
 * direct messages. Reuses POST /api/provider-messages from
 * FP3-029B6A. Optional `context` prop pre-tags the message with a
 * student/goal/session reference so the recipient gets the context
 * inline.
 */
export function SupervisionMessageComposer({ recipient, context, onClose }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.sendProviderMessage({
        to_user_id: recipient.user_id,
        body: body.trim(),
        context_type: context?.type || "general",
        context_id: context?.id || "",
        context_label: context?.label || "",
      });
      toast.success(`Message sent to ${recipient.name || "assistant"}`);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail) || "Could not send message"
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="supervision-message-composer"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 border border-[#E5E3DB]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <Envelope size={14} weight="duotone" className="text-[#1E3F33] shrink-0" />
            <h3 className="text-[14.5px] font-semibold text-[#1F2937] heading-font truncate">
              Message {recipient.name || "assistant"}
            </h3>
          </div>
          <button
            data-testid="supervision-message-close"
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#1F2937] p-1"
          >
            <X size={14} />
          </button>
        </div>
        {context && context.label && (
          <p
            data-testid="supervision-message-context"
            className="text-[11px] text-[#3F4756] bg-[#EEF1F6] px-2 py-1 rounded-md mb-3 inline-flex items-center gap-1"
          >
            <ArrowRight size={10} weight="duotone" />
            About: {context.label}
          </p>
        )}
        <textarea
          data-testid="supervision-message-body"
          autoFocus
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type your message…"
          rows={4}
          maxLength={4000}
          className="w-full text-[12.5px] border border-[#E5E3DB] rounded-md p-2 focus:outline-none focus:border-[#1E3F33] resize-none"
        />
        <p className="text-[10px] text-[#9AA0A6] mt-1">
          {body.length}/4000 · Direct supervision DM. Admin will not see this
          message.
        </p>
        <div className="flex justify-end gap-2 mt-3">
          <Button
            size="sm"
            variant="outline"
            onClick={onClose}
            className="h-8 text-[12px] border-[#E5E3DB] text-[#6B7280]"
          >
            Cancel
          </Button>
          <Button
            data-testid="supervision-message-send"
            size="sm"
            onClick={send}
            disabled={sending || !body.trim()}
            className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white"
          >
            <Envelope size={12} weight="duotone" className="mr-1" />
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
